import React, { useState, useEffect, useCallback } from "react";
import DemoUserService from "../../services/DemoUserService";
import toast from "react-hot-toast";
import { useTranslation } from "../../i18n/LanguageContext";

/**
 * Admin panel for managing demo users.
 * Accessible via Platform Admin > Demo Users in the sidebar.
 */
const DemoAdminPanel = () => {
  const { t } = useTranslation();
  const [demoUsers, setDemoUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("pending");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [approvalSettings, setApprovalSettings] = useState({
    maxRequests: 10,
    expirationDays: 3,
  });
  const [actionLoading, setActionLoading] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedEvent, setSelectedEvent] = useState("ALL");
  const [selectedStatus, setSelectedStatus] = useState("ALL");

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [pendingResult, allResult] = await Promise.all([
        DemoUserService.getPendingApprovals(),
        DemoUserService.getAllDemoUsers(),
      ]);
      if (pendingResult?.data) setPendingUsers(pendingResult.data);
      if (allResult?.data) setDemoUsers(allResult.data);
    } catch (err) {
      toast.error(t("demoAdmin.toast.loadFailed"));
      console.error("Load error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleApprove = async (registrationToken) => {
    setActionLoading(registrationToken);
    try {
      const result = await DemoUserService.approve(
        registrationToken,
        approvalSettings.maxRequests,
        approvalSettings.expirationDays,
      );
      if (result?.success) {
        toast.success(t("demoAdmin.toast.approved"));
        loadData();
      } else {
        toast.error(result?.message || "Approval failed");
      }
    } catch (err) {
      toast.error(t("demoAdmin.toast.approveFailed"));
    } finally {
      setActionLoading(null);
    }
  };

  const handleRevoke = async (registrationToken) => {
    setActionLoading(registrationToken);
    try {
      const result = await DemoUserService.revoke(registrationToken);
      if (result?.success) {
        toast.success(t("demoAdmin.toast.revoked"));
        loadData();
      } else {
        toast.error(result?.message || "Revoke failed");
      }
    } catch (err) {
      toast.error(t("demoAdmin.toast.revokeFailed"));
    } finally {
      setActionLoading(null);
    }
  };

  const handleResetUsage = async (registrationToken) => {
    setActionLoading(`reset-${registrationToken}`);
    try {
      const result = await DemoUserService.resetUsage(registrationToken);
      if (result?.success) {
        toast.success(t("demoAdmin.toast.usageReset"));
        loadData();
      } else {
        toast.error(result?.message || "Reset failed");
      }
    } catch (err) {
      toast.error(t("demoAdmin.toast.resetFailed"));
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = (registrationToken) => {
    let undone = false;
    const toastId = toast(
      (toastItem) => (
        <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span>Deleting demo user...</span>
          <button
            onClick={() => {
              undone = true;
              toast.dismiss(toastItem.id);
              toast("Delete cancelled", { icon: "↩️", duration: 2000 });
            }}
            style={{
              background: "none",
              border: "1px solid #888",
              borderRadius: "4px",
              padding: "2px 8px",
              cursor: "pointer",
              fontWeight: "bold",
              color: "inherit",
            }}
          >
            Undo
          </button>
        </span>
      ),
      { duration: 5000, icon: "🗑️" },
    );

    setTimeout(async () => {
      if (undone) return;
      toast.dismiss(toastId);
      setActionLoading(`delete-${registrationToken}`);
      try {
        const result = await DemoUserService.softDelete(registrationToken);
        if (result?.success) {
          toast.success(t("demoAdmin.toast.deleted"));
          loadData();
        } else {
          toast.error(result?.message || "Delete failed");
        }
      } catch (err) {
        toast.error(t("demoAdmin.toast.deleteFailed"));
      } finally {
        setActionLoading(null);
      }
    }, 5000);
  };

  const handleBulkApprove = async () => {
    if (selectedIds.size === 0) {
      toast.error(t("demoAdmin.toast.noUsersSelected"));
      return;
    }
    setActionLoading("bulk");
    try {
      const result = await DemoUserService.bulkApprove(
        Array.from(selectedIds),
        approvalSettings.maxRequests,
        approvalSettings.expirationDays,
      );
      if (result?.success) {
        toast.success(result.message || `${selectedIds.size} users approved`);
        setSelectedIds(new Set());
        loadData();
      } else {
        toast.error(result?.message || "Bulk approval failed");
      }
    } catch (err) {
      toast.error(t("demoAdmin.toast.bulkApprovalFailed"));
    } finally {
      setActionLoading(null);
    }
  };

  const toggleSelect = (registrationToken) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(registrationToken)) next.delete(registrationToken);
      else next.add(registrationToken);
      return next;
    });
  };

  const selectAll = () => {
    const targetUsers = activeTab === "pending" ? filteredUsers : pendingUsers;
    if (targetUsers.length === 0) return;

    const allSelected = targetUsers.every((u) =>
      selectedIds.has(u.registrationToken),
    );
    if (allSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        targetUsers.forEach((u) => next.delete(u.registrationToken));
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        targetUsers.forEach((u) => next.add(u.registrationToken));
        return next;
      });
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      UNVERIFIED: { bg: "#444", color: "#ccc" },
      PENDING_APPROVAL: { bg: "#f57c00", color: "#fff" },
      APPROVED: { bg: "#4caf50", color: "#fff" },
      EXPIRED: { bg: "#616161", color: "#ccc" },
      REVOKED: { bg: "#f44336", color: "#fff" },
    };
    const s = styles[status] || { bg: "#333", color: "#aaa" };
    return (
      <span
        style={{
          padding: "3px 10px",
          borderRadius: "12px",
          fontSize: "12px",
          fontWeight: 600,
          background: s.bg,
          color: s.color,
          display: "inline-block",
        }}
      >
        {status}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return { date: "—", time: "" };
    const d = new Date(dateStr);
    return {
      date: d.toLocaleDateString(),
      time: d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }),
    };
  };

  const containerStyle = {
    padding: "12px 16px",
    color: "var(--text-primary)",
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  };

  const cardStyle = {
    background: "var(--bg-card)",
    border: "1px solid var(--border-color)",
    borderRadius: "10px",
    padding: "12px",
    marginBottom: "12px",
  };

  const tableStyle = {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "14px",
  };

  const thStyle = {
    textAlign: "left",
    padding: "8px 10px",
    borderBottom: "1px solid var(--border-color)",
    color: "var(--text-secondary)",
    fontWeight: 600,
    fontSize: "12px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  };

  const tdStyle = {
    padding: "8px 10px",
    borderBottom: "1px solid var(--border-color)",
    color: "var(--text-primary)",
  };

  const tabStyle = (active) => ({
    padding: "8px 14px",
    background: active ? "#2196f3" : "transparent",
    color: active ? "#fff" : "var(--text-secondary)",
    border: "1px solid " + (active ? "#2196f3" : "var(--border-color)"),
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: active ? 600 : 400,
    marginRight: "8px",
  });

  const btnStyle = (variant = "primary") => ({
    padding: "6px 14px",
    borderRadius: "6px",
    border: "none",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: 500,
    ...(variant === "primary"
      ? { background: "#4caf50", color: "#fff" }
      : variant === "danger"
        ? { background: "#f44336", color: "#fff" }
        : {
            background: "var(--bg-primary)",
            color: "var(--text-secondary)",
            border: "1px solid var(--border-color)",
          }),
  });

  if (loading) {
    return (
      <div style={containerStyle}>
        <h2>Demo Users</h2>
        <p>Loading...</p>
      </div>
    );
  }

  const displayedUsers = activeTab === "pending" ? pendingUsers : demoUsers;
  const normalizedSearchQuery = searchQuery.trim().toLowerCase();
  const numericSearchQuery = normalizedSearchQuery.replace(/\D/g, "");
  const availableEvents = Array.from(
    new Set(
      [...pendingUsers, ...demoUsers]
        .map((user) => (user.eventName || "").trim())
        .filter(Boolean),
    ),
  ).sort((a, b) => a.localeCompare(b));
  const availableStatuses = Array.from(
    new Set(
      [...pendingUsers, ...demoUsers]
        .map((user) => (user.status || "").trim())
        .filter(Boolean),
    ),
  ).sort((a, b) => a.localeCompare(b));

  const filteredUsers = displayedUsers.filter((user) => {
    const userEventName = (user.eventName || "").trim();
    const matchesEvent =
      selectedEvent === "ALL" || userEventName === selectedEvent;
    const userStatus = (user.status || "").trim();
    const matchesStatus =
      selectedStatus === "ALL" || userStatus === selectedStatus;

    if (!matchesEvent) return false;
    if (!matchesStatus) return false;
    if (!normalizedSearchQuery) return true;

    const email = (user.email || "").toLowerCase();
    const phone = (user.phone || "").toLowerCase();
    const phoneDigits = (user.phone || "").replace(/\D/g, "");

    return (
      email.includes(normalizedSearchQuery) ||
      phone.includes(normalizedSearchQuery) ||
      (numericSearchQuery.length > 0 &&
        phoneDigits.includes(numericSearchQuery))
    );
  });

  const allFilteredPendingSelected =
    activeTab === "pending" &&
    filteredUsers.length > 0 &&
    filteredUsers.every((u) => selectedIds.has(u.registrationToken));

  return (
    <div style={containerStyle}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "12px",
        }}
      >
        <h2 style={{ margin: 0, color: "var(--text-primary)" }}>
          Demo User Management
        </h2>
        <button onClick={loadData} style={btnStyle("secondary")}>
          🔄 Refresh
        </button>
      </div>

      {/* Stats */}
      <div
        style={{
          display: "flex",
          gap: "8px",
          marginBottom: "12px",
          flexWrap: "wrap",
        }}
      >
        {[
          { label: "Pending", count: pendingUsers.length, color: "#f57c00" },
          {
            label: "Approved",
            count: demoUsers.filter((u) => u.status === "APPROVED").length,
            color: "#4caf50",
          },
          { label: "Total", count: demoUsers.length, color: "#2196f3" },
        ].map((stat) => (
          <div
            key={stat.label}
            style={{
              ...cardStyle,
              flex: "1",
              minWidth: "120px",
              textAlign: "center",
              marginBottom: 0,
              padding: "10px 8px",
            }}
          >
            <div
              style={{ fontSize: "34px", fontWeight: 700, color: stat.color }}
            >
              {stat.count}
            </div>
            <div
              style={{
                fontSize: "12px",
                color: "var(--text-secondary)",
                marginTop: "2px",
              }}
            >
              {stat.label}
            </div>
          </div>
        ))}
      </div>

      {/* Demo Info Section */}
      <div style={cardStyle}>
        <h3
          style={{
            margin: "0 0 8px",
            fontSize: "14px",
            color: "var(--text-primary)",
          }}
        >
          Demo Registration Info
        </h3>
        <div style={{ fontSize: "13px", color: "var(--text-secondary)" }}>
          <p style={{ margin: "0 0 8px" }}>
            <strong>Registration URL:</strong>{" "}
            <a
              href="https://shield.kinprotect.com/demo/register"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#2196f3", textDecoration: "none" }}
            >
              https://shield.kinprotect.com/demo/register
            </a>
          </p>
          <p style={{ margin: "0" }}>
            Registered demo users can forward emails and texts to KinShield and
            receive email and text responses at their registered email address
            and phone number.
          </p>
        </div>
      </div>

      {/* Approval settings */}
      {activeTab === "pending" && pendingUsers.length > 0 && (
        <div style={cardStyle}>
          <h3
            style={{
              margin: "0 0 8px",
              fontSize: "14px",
              color: "var(--text-primary)",
            }}
          >
            Approval Settings
          </h3>
          <div
            style={{
              display: "flex",
              gap: "12px",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <label style={{ fontSize: "13px", color: "var(--text-secondary)" }}>
              Max Requests:
              <input
                type="number"
                min="1"
                max="100"
                value={approvalSettings.maxRequests}
                onChange={(e) =>
                  setApprovalSettings((s) => ({
                    ...s,
                    maxRequests: parseInt(e.target.value) || 10,
                  }))
                }
                style={{
                  marginLeft: "8px",
                  padding: "6px 10px",
                  background: "var(--bg-input, var(--bg-primary))",
                  border: "1px solid var(--border-color)",
                  borderRadius: "6px",
                  color: "var(--text-primary)",
                  width: "70px",
                }}
              />
            </label>
            <label style={{ fontSize: "13px", color: "var(--text-secondary)" }}>
              Expiration (days):
              <input
                type="number"
                min="1"
                max="30"
                value={approvalSettings.expirationDays}
                onChange={(e) =>
                  setApprovalSettings((s) => ({
                    ...s,
                    expirationDays: parseInt(e.target.value) || 3,
                  }))
                }
                style={{
                  marginLeft: "8px",
                  padding: "6px 10px",
                  background: "var(--bg-input, var(--bg-primary))",
                  border: "1px solid var(--border-color)",
                  borderRadius: "6px",
                  color: "var(--text-primary)",
                  width: "70px",
                }}
              />
            </label>
            {selectedIds.size > 0 && (
              <button
                onClick={handleBulkApprove}
                style={btnStyle("primary")}
                disabled={actionLoading === "bulk"}
              >
                {actionLoading === "bulk"
                  ? "..."
                  : `✅ Approve Selected (${selectedIds.size})`}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{ marginBottom: "10px" }}>
        <button
          style={tabStyle(activeTab === "pending")}
          onClick={() => setActiveTab("pending")}
        >
          Pending ({pendingUsers.length})
        </button>
        <button
          style={tabStyle(activeTab === "all")}
          onClick={() => setActiveTab("all")}
        >
          All Users ({demoUsers.length})
        </button>
      </div>

      <div
        style={{
          ...cardStyle,
          marginBottom: "10px",
          display: "flex",
          gap: "10px",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: "420px",
            minWidth: "240px",
            position: "relative",
          }}
        >
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by email or phone"
            style={{
              width: "100%",
              padding: "10px 36px 10px 12px",
              background: "var(--bg-input, var(--bg-primary))",
              border: "1px solid var(--border-color)",
              borderRadius: "8px",
              color: "var(--text-primary)",
              fontSize: "14px",
            }}
          />
          {searchQuery.trim().length > 0 && (
            <button
              type="button"
              onClick={() => setSearchQuery("")}
              aria-label="Clear search"
              style={{
                position: "absolute",
                right: "8px",
                top: "50%",
                transform: "translateY(-50%)",
                width: "22px",
                height: "22px",
                borderRadius: "50%",
                border: "1px solid var(--border-color)",
                background: "var(--bg-primary)",
                color: "var(--text-secondary)",
                cursor: "pointer",
                fontSize: "12px",
                lineHeight: "1",
                padding: 0,
              }}
            >
              ×
            </button>
          )}
        </div>
        <select
          value={selectedEvent}
          onChange={(e) => setSelectedEvent(e.target.value)}
          style={{
            width: "100%",
            maxWidth: "360px",
            minWidth: "220px",
            padding: "10px 12px",
            background: "var(--bg-input, var(--bg-primary))",
            border: "1px solid var(--border-color)",
            borderRadius: "8px",
            color: "var(--text-primary)",
            fontSize: "14px",
          }}
        >
          <option value="ALL">All Events</option>
          {availableEvents.map((eventName) => (
            <option key={eventName} value={eventName}>
              {eventName}
            </option>
          ))}
        </select>
        <select
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
          style={{
            width: "100%",
            maxWidth: "260px",
            minWidth: "180px",
            padding: "10px 12px",
            background: "var(--bg-input, var(--bg-primary))",
            border: "1px solid var(--border-color)",
            borderRadius: "8px",
            color: "var(--text-primary)",
            fontSize: "14px",
          }}
        >
          <option value="ALL">All Statuses</option>
          {availableStatuses.map((status) => (
            <option key={status} value={status}>
              {status}
            </option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div style={cardStyle}>
        {filteredUsers.length === 0 ? (
          <p style={{ textAlign: "center", color: "#667", padding: "20px" }}>
            {displayedUsers.length === 0
              ? activeTab === "pending"
                ? "No pending approvals"
                : "No demo users yet"
              : "No users match your filters"}
          </p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  {activeTab === "pending" && (
                    <th style={thStyle}>
                      <input
                        type="checkbox"
                        checked={allFilteredPendingSelected}
                        onChange={selectAll}
                      />
                    </th>
                  )}
                  <th style={thStyle}>ID</th>
                  <th style={thStyle}>Name</th>
                  <th style={thStyle}>Email</th>
                  <th style={thStyle}>Phone</th>
                  <th style={thStyle}>Event</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Verified</th>
                  <th style={thStyle}>Usage</th>
                  <th style={thStyle}>Expires</th>
                  <th style={thStyle}>Created</th>
                  <th style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <tr
                    key={user.registrationToken}
                    style={{ transition: "background 0.2s" }}
                  >
                    {activeTab === "pending" && (
                      <td style={tdStyle}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(user.registrationToken)}
                          onChange={() => toggleSelect(user.registrationToken)}
                        />
                      </td>
                    )}
                    <td style={tdStyle}>{user.id}</td>
                    <td style={tdStyle}>
                      {user.firstName || user.lastName ? (
                        <div style={{ lineHeight: "1.4" }}>
                          <div>{user.firstName || ""}</div>
                          <div>{user.lastName || ""}</div>
                        </div>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td style={tdStyle}>{user.email || "—"}</td>
                    <td style={tdStyle}>{user.phone || "—"}</td>
                    <td style={tdStyle}>{user.eventName || "—"}</td>
                    <td style={tdStyle}>{getStatusBadge(user.status)}</td>
                    <td style={tdStyle}>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "2px",
                          fontSize: "13px",
                        }}
                      >
                        {user.email && (
                          <span>📧 {user.emailVerified ? "✅" : "❌"}</span>
                        )}
                        {user.phone && (
                          <span>📱 {user.phoneVerified ? "✅" : "❌"}</span>
                        )}
                      </div>
                    </td>
                    <td
                      style={{
                        ...tdStyle,
                        color:
                          user.maxRequests > 0 &&
                          user.requestsUsed >= user.maxRequests
                            ? "#f44336"
                            : tdStyle.color,
                        fontWeight:
                          user.maxRequests > 0 &&
                          user.requestsUsed >= user.maxRequests
                            ? 700
                            : 400,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "4px",
                          alignItems: "flex-start",
                        }}
                      >
                        <span>
                          {user.requestsUsed}/{user.maxRequests}
                        </span>
                        {user.maxRequests > 0 &&
                          user.requestsUsed >= user.maxRequests && (
                            <span
                              style={{
                                fontSize: "11px",
                                fontWeight: 700,
                                color: "#f44336",
                                background: "#ffebee",
                                border: "1px solid #f44336",
                                borderRadius: "10px",
                                padding: "2px 8px",
                                textTransform: "uppercase",
                                letterSpacing: "0.3px",
                              }}
                            >
                              Limit Reached
                            </span>
                          )}
                      </div>
                    </td>
                    <td style={tdStyle}>
                      <div style={{ lineHeight: "1.4" }}>
                        <div>{formatDate(user.expiresAt).date}</div>
                        <div
                          style={{
                            fontSize: "12px",
                            color: "var(--text-secondary)",
                          }}
                        >
                          {formatDate(user.expiresAt).time}
                        </div>
                      </div>
                    </td>
                    <td style={tdStyle}>
                      <div style={{ lineHeight: "1.4" }}>
                        <div>{formatDate(user.createdAt).date}</div>
                        <div
                          style={{
                            fontSize: "12px",
                            color: "var(--text-secondary)",
                          }}
                        >
                          {formatDate(user.createdAt).time}
                        </div>
                      </div>
                    </td>
                    <td style={tdStyle}>
                      <div style={{ display: "flex", gap: "4px" }}>
                        {user.status === "PENDING_APPROVAL" && (
                          <button
                            onClick={() =>
                              handleApprove(user.registrationToken)
                            }
                            style={btnStyle("primary")}
                            disabled={actionLoading === user.registrationToken}
                          >
                            {actionLoading === user.registrationToken
                              ? "..."
                              : "Approve"}
                          </button>
                        )}
                        {(user.status === "APPROVED" ||
                          user.status === "PENDING_APPROVAL") && (
                          <button
                            onClick={() => handleRevoke(user.registrationToken)}
                            style={btnStyle("danger")}
                            disabled={actionLoading === user.registrationToken}
                          >
                            {actionLoading === user.registrationToken
                              ? "..."
                              : "Revoke"}
                          </button>
                        )}
                        {user.status === "APPROVED" &&
                          user.requestsUsed > 0 && (
                            <button
                              onClick={() =>
                                handleResetUsage(user.registrationToken)
                              }
                              style={btnStyle("secondary")}
                              disabled={
                                actionLoading ===
                                `reset-${user.registrationToken}`
                              }
                              title="Reset usage counter to 0"
                            >
                              {actionLoading ===
                              `reset-${user.registrationToken}`
                                ? "..."
                                : "Reset Usage"}
                            </button>
                          )}
                        <button
                          onClick={() => handleDelete(user.registrationToken)}
                          style={{
                            ...btnStyle("danger"),
                            opacity: 0.7,
                            fontSize: "11px",
                          }}
                          disabled={
                            actionLoading === `delete-${user.registrationToken}`
                          }
                          title="Soft delete — allows re-registration with same email/phone"
                        >
                          {actionLoading === `delete-${user.registrationToken}`
                            ? "..."
                            : "Delete"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default DemoAdminPanel;
